import React, { useState, useEffect, useRef } from 'react';
import { api } from '../context/AppContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import {
  Share2, Download, Loader2, Eye, EyeOff, Shield, Trophy,
  Flame, Scale, Calendar, Award, Check
} from 'lucide-react';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * StoryGenerator — Generates a shareable Instagram Story image
 * with user stats, optional progress photo, and privacy controls.
 * @param {Object} props
 * @param {string} props.language - 'fr' or 'en'
 */
export function StoryGenerator({ language }) {
  const [storyData, setStoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [consent, setConsent] = useState(false);
  const [blurFace, setBlurFace] = useState(false);
  const [statsOnly, setStatsOnly] = useState(false);
  const storyRef = useRef(null);

  useEffect(() => {
    api.get('/evolution/story-data')
      .then(res => setStoryData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleGenerate = async () => {
    if (!storyRef.current) return;
    if (!consent) {
      toast.error(language === 'fr'
        ? 'Veuillez accepter le consentement de partage.'
        : 'Please accept the sharing consent.');
      return;
    }
    setGenerating(true);
    try {
      const dataUrl = await toPng(storyRef.current, {
        pixelRatio: 3,
        cacheBust: true,
        backgroundColor: '#0C0C0C',
        width: 360,
        height: 640,
      });

      if (navigator.share && navigator.canShare) {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], 'yumix-story.png', { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: 'YUMIX Story',
            text: language === 'fr' ? 'Ma transformation Extra Miles!' : 'My Extra Miles transformation!'
          });
          toast.success(language === 'fr' ? 'Partage réussi !' : 'Shared!');
          setGenerating(false);
          return;
        }
      }
      // Fallback: download
      const link = document.createElement('a');
      link.download = 'yumix-story.png';
      link.href = dataUrl;
      link.click();
      toast.success(language === 'fr' ? 'Image telechargee !' : 'Image downloaded!');
    } catch (err) {
      console.error('Story generation error:', err);
      toast.error(language === 'fr' ? 'Erreur de generation' : 'Generation error');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!storyData) return null;

  const d = storyData;
  const hasPhoto = d.latest_photo && !statsOnly;

  return (
    <div className="space-y-4" data-testid="story-generator">
      {/* Privacy Controls */}
      <Card className="bg-card border-border">
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-bold">{language === 'fr' ? 'Options de confidentialite' : 'Privacy Options'}</h3>
          </div>

          <div className="flex items-center justify-between py-1.5">
            <label className="text-sm text-muted-foreground flex items-center gap-2">
              <EyeOff className="w-3.5 h-3.5" />
              {language === 'fr' ? 'Mode stats uniquement (pas de photo)' : 'Stats only mode (no photo)'}
            </label>
            <Switch
              checked={statsOnly}
              onCheckedChange={setStatsOnly}
              data-testid="story-stats-only-toggle"
            />
          </div>

          {!statsOnly && d.latest_photo && (
            <div className="flex items-center justify-between py-1.5">
              <label className="text-sm text-muted-foreground flex items-center gap-2">
                <Eye className="w-3.5 h-3.5" />
                {language === 'fr' ? 'Flouter le visage' : 'Blur face'}
              </label>
              <Switch
                checked={blurFace}
                onCheckedChange={setBlurFace}
                data-testid="story-blur-face-toggle"
              />
            </div>
          )}

          <div className="flex items-center gap-3 pt-2 border-t border-border/50">
            <button
              onClick={() => setConsent(!consent)}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                consent ? 'bg-primary border-primary' : 'border-muted-foreground'
              }`}
              data-testid="story-consent-checkbox"
            >
              {consent && <Check className="w-3 h-3 text-primary-foreground" />}
            </button>
            <p className="text-[11px] text-muted-foreground leading-tight">
              {language === 'fr'
                ? 'J\'autorise le partage de mes statistiques et/ou photo dans cette story. Je comprends que cette image sera visible publiquement.'
                : 'I authorize sharing my stats and/or photo in this story. I understand this image will be publicly visible.'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Story Preview */}
      <div className="flex justify-center">
        <div
          ref={storyRef}
          className="relative overflow-hidden"
          style={{
            width: 360,
            height: 640,
            background: 'linear-gradient(180deg, #0C0C0C 0%, #1A1A2E 40%, #16213E 70%, #0C0C0C 100%)',
            borderRadius: 20,
            fontFamily: "'Inter', sans-serif",
          }}
          data-testid="story-preview"
        >
          {/* Top brand bar */}
          <div style={{ padding: '24px 24px 0', textAlign: 'center' }}>
            <div style={{
              display: 'inline-block',
              padding: '4px 16px',
              borderRadius: 20,
              border: '1.5px solid #FF6B35',
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 3,
              color: '#FF6B35',
              textTransform: 'uppercase',
            }}>
              Extra Miles
            </div>
          </div>

          {/* Photo section */}
          {hasPhoto && (
            <div style={{ padding: '16px 24px 0', textAlign: 'center' }}>
              <div style={{
                width: 120,
                height: 160,
                borderRadius: 16,
                overflow: 'hidden',
                margin: '0 auto',
                border: '2px solid rgba(255, 107, 53, 0.4)',
              }}>
                <img
                  src={`${BACKEND_URL}${d.latest_photo.url}`}
                  alt="Progress"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    filter: blurFace ? 'blur(12px)' : 'none',
                  }}
                  crossOrigin="anonymous"
                />
              </div>
            </div>
          )}

          {/* Name & Grade */}
          <div style={{ padding: `${hasPhoto ? 12 : 40}px 24px 0`, textAlign: 'center' }}>
            <p style={{
              fontSize: 28,
              fontWeight: 900,
              color: '#FFFFFF',
              textTransform: 'uppercase',
              letterSpacing: 1,
              lineHeight: 1.1,
              margin: 0,
            }}>
              {d.first_name}
            </p>
            <p style={{
              fontSize: 12,
              fontWeight: 700,
              color: '#FF6B35',
              textTransform: 'uppercase',
              letterSpacing: 3,
              marginTop: 4,
            }}>
              {d.grade}
            </p>
          </div>

          {/* Stats grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
            padding: '20px 24px',
          }}>
            {/* Weight Lost */}
            <div style={{
              background: 'rgba(255,107,53,0.08)',
              border: '1px solid rgba(255,107,53,0.25)',
              borderRadius: 14,
              padding: '14px 12px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
                {language === 'fr' ? 'Perdu' : 'Lost'}
              </div>
              <div style={{ fontSize: 28, fontWeight: 900, color: '#FF6B35', lineHeight: 1.2 }}>
                {d.weight_lost > 0 ? d.weight_lost : '0'}
              </div>
              <div style={{ fontSize: 11, color: '#999', fontWeight: 600 }}>lbs</div>
            </div>

            {/* Days Active */}
            <div style={{
              background: 'rgba(16,185,129,0.08)',
              border: '1px solid rgba(16,185,129,0.25)',
              borderRadius: 14,
              padding: '14px 12px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
                {language === 'fr' ? 'Jours actifs' : 'Days Active'}
              </div>
              <div style={{ fontSize: 28, fontWeight: 900, color: '#10B981', lineHeight: 1.2 }}>
                {d.days_active}
              </div>
              <div style={{ fontSize: 11, color: '#999', fontWeight: 600 }}>
                {language === 'fr' ? 'jours' : 'days'}
              </div>
            </div>

            {/* Streak */}
            <div style={{
              background: 'rgba(255,215,0,0.08)',
              border: '1px solid rgba(255,215,0,0.25)',
              borderRadius: 14,
              padding: '14px 12px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
                Streak
              </div>
              <div style={{ fontSize: 28, fontWeight: 900, color: '#FFD700', lineHeight: 1.2 }}>
                {d.weigh_streak}
              </div>
              <div style={{ fontSize: 11, color: '#999', fontWeight: 600 }}>
                {language === 'fr' ? 'jours' : 'days'}
              </div>
            </div>

            {/* Badges */}
            <div style={{
              background: 'rgba(139,92,246,0.08)',
              border: '1px solid rgba(139,92,246,0.25)',
              borderRadius: 14,
              padding: '14px 12px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
                Badges
              </div>
              <div style={{ fontSize: 28, fontWeight: 900, color: '#8B5CF6', lineHeight: 1.2 }}>
                {d.badges_count}
              </div>
              <div style={{ fontSize: 11, color: '#999', fontWeight: 600 }}>
                {language === 'fr' ? 'gagnes' : 'earned'}
              </div>
            </div>
          </div>

          {/* Motivational quote */}
          <div style={{ padding: '0 24px', textAlign: 'center' }}>
            <p style={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.6)',
              fontStyle: 'italic',
              lineHeight: 1.5,
              margin: 0,
            }}>
              {d.weight_lost >= 10
                ? (language === 'fr' ? '"La discipline bat le talent quand le talent manque de discipline."' : '"Discipline beats talent when talent lacks discipline."')
                : (language === 'fr' ? '"Chaque repetition te rapproche de ta meilleure version."' : '"Every rep brings you closer to your best self."')
              }
            </p>
          </div>

          {/* Footer */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '16px 24px',
            textAlign: 'center',
            background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
          }}>
            <p style={{
              fontSize: 10,
              color: 'rgba(255,255,255,0.35)',
              letterSpacing: 2,
              textTransform: 'uppercase',
              fontWeight: 700,
              margin: 0,
            }}>
              extramiles.ca
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button
          onClick={handleGenerate}
          disabled={generating || !consent}
          className="flex-1"
          data-testid="story-download-btn"
        >
          {generating ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          {language === 'fr' ? 'Telecharger' : 'Download'}
        </Button>
        <Button
          onClick={handleGenerate}
          disabled={generating || !consent}
          variant="outline"
          className="flex-1"
          data-testid="story-share-btn"
        >
          <Share2 className="w-4 h-4 mr-2" />
          {language === 'fr' ? 'Partager' : 'Share'}
        </Button>
      </div>

      {!consent && (
        <p className="text-[11px] text-center text-muted-foreground">
          {language === 'fr'
            ? 'Acceptez le consentement ci-dessus pour activer le partage.'
            : 'Accept the consent above to enable sharing.'}
        </p>
      )}
    </div>
  );
}

export default StoryGenerator;
